import React, { useEffect, useState } from 'react';
import dpdpActData from '../../static/dpdp-act.json';
import dpdpRulesData from '../../static/dpdp-rules.json';

// ─── Precise path-based cross-reference maps ──────────────────────────────────
// Each entry: { clauseNum?, subClause?, subSubClause?, whole?, linkedNums }
// whole:true → show below article title  |  omit → show inline after that exact clause/sub-clause
const ACT_PRECISE_REFS = {
  5:  [{ clauseNum: 1, subSubClause: 'iii', linkedNums: [3] }],
  6:  [{ clauseNum: 9, linkedNums: [4] }],
  7:  [{ renderBeforeIllustration: true, afterSubClause: 'b', linkedNums: [5] }],
  8:  [
    { clauseNum: 5, linkedNums: [6] },
    { clauseNum: 6, linkedNums: [7] },
    { renderBeforeClause: 9, linkedNums: [8] },
    { clauseNum: 9, linkedNums: [9] },
  ],
  9:  [{ clauseNum: 1, linkedNums: [10, 11] }, { clauseNum: 4, linkedNums: [12] }],
  10: [{ clauseNum: 2, subClause: 'c', subSubClause: 'i', linkedNums: [13] }],
  13: [{ clauseNum: 1, linkedNums: [14] }, { clauseNum: 2, linkedNums: [14] }],
  14: [{ whole: true, linkedNums: [14] }],
  16: [{ clauseNum: 1, linkedNums: [15] }],
  17: [{ clauseNum: 2, subClause: 'b', linkedNums: [16] }],
  19: [{ clauseNum: 1, linkedNums: [17] }, { clauseNum: 2, linkedNums: [17] }],
  20: [{ clauseNum: 1, linkedNums: [18] }],
  23: [{ clauseNum: 1, linkedNums: [19] }],
  24: [{ whole: true, linkedNums: [21] }],
  29: [{ clauseNum: 2, linkedNums: [22] }],
};

// Schedule cross-references for Act sections
// Each entry: { clauseNum?, subClause?, whole?, scheduleNum, schedulePart? }
const ACT_SCHEDULE_REFS = {
  6:  [{ clauseNum: 9, scheduleNum: 1, schedulePart: 'A' }],
  7:  [{ renderBeforeIllustration: true, afterSubClause: 'b', scheduleNum: 2 }],
  8:  [{ renderBeforeClause: 9, scheduleNum: 3 }],
  17: [{ clauseNum: 2, subClause: 'b', scheduleNum: 2 }],
  20: [{ clauseNum: 1, scheduleNum: 5 }],
};
const RULES_PRECISE_REFS = {
  3:  [{ whole: true, linkedNums: [5] }],
  4:  [{ whole: true, linkedNums: [6] }],
  5:  [{ whole: true, linkedNums: [7] }],
  6:  [{ whole: true, linkedNums: [8] }],  7:  [{ whole: true, linkedNums: [8] }],
  8:  [{ whole: true, linkedNums: [8] }],  9:  [{ whole: true, linkedNums: [8] }],
  10: [{ whole: true, linkedNums: [9] }],  11: [{ whole: true, linkedNums: [9] }],
  12: [{ whole: true, linkedNums: [9] }],
  13: [{ clauseNum: 1, linkedNums: [10] }],
  14: [{ clauseNum: 1, linkedNums: [13] }, { clauseNum: 3, linkedNums: [13] }, { clauseNum: 4, linkedNums: [14] }],
  15: [{ whole: true, linkedNums: [16] }],
  16: [{ whole: true, linkedNums: [17] }],
  17: [{ whole: true, linkedNums: [19] }],
  18: [{ whole: true, linkedNums: [20] }],
  19: [{ whole: true, linkedNums: [23] }],
  21: [{ whole: true, linkedNums: [24] }],
  22: [{ clauseNum: 2, linkedNums: [29] }],
};

// Returns true if the current ctx position matches this ref's target
// Only checks the fields that are explicitly specified in the ref.
function matchesContext(ref, ctx) {
  if (ref.whole) return false;
  if (ref.renderBeforeClause !== undefined) return false;
  if (ref.renderBeforeSubClause !== undefined) return false;
  if (ref.renderBeforeIllustration) return false;
  if (ref.clauseNum !== undefined && ctx.clause !== ref.clauseNum) return false;
  if (ref.subClause !== undefined && ctx.subClause !== ref.subClause) return false;
  if (ref.subSubClause !== undefined && ctx.subSubClause !== ref.subSubClause) return false;
  // For a clause-only ref (no sub-levels), must be exactly at clause level
  if (ref.clauseNum !== undefined && ref.subClause === undefined && ref.subSubClause === undefined && ctx.subClause !== null) return false;
  // For a subClause-only ref (no subSubClause), must be exactly at subClause level
  if (ref.subClause !== undefined && ref.subSubClause === undefined && ctx.subSubClause !== null) return false;
  return true;
}

function findArticleAcrossChapters(data, num) {
  for (const ch of data.chapters || []) {
    const a = (ch.articles || []).find(x => x.number === num);
    if (a) return { ...a, chapterName: ch.name, chapterNum: ch.number };
  }
  return null;
}

// Expandable panel used both inline (beside a clause) and below the article title
function RefPanel({ linkedNums, otherData, otherPrefix, otherLabel, otherColor, navigateMode }) {
  const [open, setOpen] = useState(false);
  const articles = linkedNums.map(n => findArticleAcrossChapters(otherData, n)).filter(Boolean);
  if (!articles.length) return null;

  // Navigate mode: clicking goes directly to that article instead of expanding inline content
  if (navigateMode) {
    return (
      <div className="my-2 border rounded-lg overflow-hidden" style={{ borderColor: otherColor + '40' }}>
        {articles.map((art, i) => (
          <Link
            key={i}
            to={`/law-detail?id=${otherData.id}&chapter=${art.chapterNum}&article=${art.number}`}
            className="block sm:flex w-full px-3 py-3 transition-colors group hover:bg-opacity-20"
            style={{ background: otherColor + '08' }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: otherColor }} />
                <span className="text-xs font-bold uppercase tracking-wider text-gray-600" style={{ color: otherColor }}>
                  Corresponding {otherLabel}
                </span>
              </div>
              <div className="text-xs font-semibold text-gray-700 leading-relaxed sm:ml-0">
                {otherPrefix} {art.number} — {art.title}
              </div>
            </div>
            <div className="flex justify-end mt-2 sm:mt-0 sm:ml-3">
              <ExternalLink className="w-4 h-4 flex-shrink-0 group-hover:text-blue-600 transition-colors" style={{ color: otherColor }} />
            </div>
          </Link>
        ))}
      </div>
    );
  }

  const badge = articles.map(a => `${otherPrefix} ${a.number}`).join(' & ');
  return (
    <div className="my-2 border rounded-lg overflow-hidden" style={{ borderColor: otherColor + '40' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2 text-left transition-colors"
        style={{ background: open ? otherColor + '12' : otherColor + '08' }}
      >
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: otherColor }} />
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: otherColor }}>
            Corresponding {otherLabel}
          </span>
          <span className="text-xs font-semibold text-gray-700 ml-1">{badge}</span>
        </div>
        <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="divide-y" style={{ borderColor: otherColor + '20' }}>
          {articles.map((art, i) => (
            <div key={i} className="px-4 py-3 bg-white">
              <p className="text-sm font-bold text-gray-900 mb-2">
                {otherPrefix} {art.number} — {art.title}
              </p>
              <div className="border-l-2 pl-3 prose prose-sm prose-gray max-w-none text-justify" style={{ borderColor: otherColor + '60' }}>
                {art.content
                  ? <ReactMarkdown>{art.content}</ReactMarkdown>
                  : <span className="italic text-gray-400">Content not available.</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Expandable panel for schedule cross-references (dropdown like RefPanel)
function ScheduleRefPanel({ scheduleNum, schedulePart, lawData }) {
  const [open, setOpen] = useState(false);
  const schedule = (lawData.schedules || []).find(s => s.number === scheduleNum);
  if (!schedule) return null;
  const color = '#0891b2';
  const ordinalShort = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th'];
  const shortName = `${ordinalShort[scheduleNum - 1] || scheduleNum} Sch.`;
  const badge = schedulePart ? `${shortName} Part ${schedulePart}` : shortName;
  const partObj = schedulePart && schedule.parts
    ? schedule.parts.find(p => p.part === schedulePart)
    : null;
  const title = partObj ? partObj.title : schedule.title || '';
  const content = partObj ? partObj.content : schedule.content || '';
  const points = partObj ? partObj.points : schedule.points || null;
  const table = partObj ? partObj.table : schedule.table || null;

  const renderPoints = (pts) => (
    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
      {pts.map((pt, idx) => (
        <li key={idx}>
          {typeof pt === 'string' ? pt : (
            <div>
              <span>{pt.text}</span>
              {pt.subpoints && (
                <ol className="list-[lower-alpha] list-inside ml-6 mt-1 space-y-1">
                  {pt.subpoints.map((sub, si) => <li key={si} className="text-gray-600">{sub}</li>)}
                </ol>
              )}
            </div>
          )}
        </li>
      ))}
    </ol>
  );

  return (
    <div className="my-2 border rounded-lg overflow-hidden" style={{ borderColor: color + '40' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2 text-left transition-colors"
        style={{ background: open ? color + '12' : color + '08' }}
      >
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>
            Corresponding Schedule
          </span>
          <span className="text-xs font-semibold text-gray-700 ml-1">{badge}</span>
        </div>
        <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-4 py-3 bg-white">
          {title && <p className="text-sm font-bold text-gray-900 mb-2">{badge} — {title}</p>}
          <div className="border-l-2 pl-3 prose prose-sm prose-gray max-w-none text-justify" style={{ borderColor: color + '60' }}>
            {content
              ? <ReactMarkdown>{content}</ReactMarkdown>
              : points
                ? renderPoints(points)
                : table
                  ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full border-collapse border border-gray-300 text-xs">
                        <thead>
                          <tr className="bg-gray-50">
                            {table.columns.map((col, ci) => (
                              <th key={ci} className="border border-gray-300 px-2 py-1.5 text-left font-semibold text-gray-800">{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {table.rows.map((row, ri) => {
                            const keys = Object.keys(row);
                            return (
                              <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                {table.columns.map((_, ci) => (
                                  <td key={ci} className="border border-gray-300 px-2 py-1.5 text-gray-700 align-top whitespace-pre-line">
                                    {row[keys[ci]] || ''}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
                  : <span className="italic text-gray-400">Content not available.</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper to convert to title case, skipping common words
function toTitleCase(str) {
  if (!str) return '';
  const skip = ['of', 'for', 'and', 'the', 'in', 'on', 'at', 'to', 'by', 'with', 'a', 'an', 'but', 'or', 'nor', 'as', 'from', 'into', 'like', 'over', 'after', 'than', 'up', 'down', 'out', 'off', 'so', 'yet', 'per', 'via'];
  return str
    .split(' ')
    .map((word, i, arr) => {
      if (word.length === 0) return '';
      const lower = word.toLowerCase();
      if (i !== 0 && i !== arr.length - 1 && skip.includes(lower)) {
        return lower;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

// Smart preprocessing: distinguish lettered subclauses from roman numeral sub-subclauses
// by tracking the alphabetical sequence in the content.
// Resets the sequence whenever a numbered clause like (1), (2) appears between
// two letter markers, so each numbered clause gets a fresh (a),(b),(c)... sequence.
function classifyClauseMarkers(text) {
  // Split into paragraphs
  const paragraphs = text.split('\n\n');
  // Allow optional opening quote before the marker (e.g. "(i) quoted text)
  const markerRegex = /^"?\(([a-z]+)\)\s/;
  const numericMarkerRegex = /^\(\d+\)/;

  // First pass: collect all letter-marker indices
  const markerInfos = [];
  paragraphs.forEach((p, idx) => {
    const m = p.trim().match(markerRegex);
    if (m) {
      markerInfos.push({ idx, marker: m[1] });
    }
  });

  // Build a set of paragraph indices where a numbered clause header appears
  const resetPoints = new Set();
  paragraphs.forEach((p, idx) => {
    if (numericMarkerRegex.test(p.trim())) resetPoints.add(idx);
  });

  // Second pass: track alphabetical sequence to classify each marker
  const romanIndices = new Set();
  let expectedNextLetter = null;

  for (let i = 0; i < markerInfos.length; i++) {
    const { idx, marker } = markerInfos[i];

    // If a numbered clause appears between the previous letter marker and this one,
    // reset the sequence so the new clause's (a),(b),(c)... starts fresh.
    const prevIdx = i === 0 ? -1 : markerInfos[i - 1].idx;
    const hasResetBetween = [...resetPoints].some(rp => rp > prevIdx && rp <= idx);
    if (hasResetBetween) expectedNextLetter = null;

    if (marker.length === 1) {
      // Single character - could be letter or roman numeral
      if (expectedNextLetter === null) {
        // Starting a new sequence — but first peek ahead:
        // if the very next marker (without a reset between) is multi-char
        // and begins with the current marker, we're in a roman numeral
        // sequence (e.g. (i) → (ii)), not an alphabetical one.
        const nextInfo = markerInfos[i + 1];
        const nextHasReset = nextInfo && [...resetPoints].some(rp => rp > idx && rp <= nextInfo.idx);
        if (nextInfo && !nextHasReset && nextInfo.marker.length > 1 && nextInfo.marker.startsWith(marker)) {
          romanIndices.add(idx);
          continue; // skip setting expectedNextLetter
        }
        // Otherwise start a normal alphabetical sequence
        expectedNextLetter = marker;
      }

      if (marker === expectedNextLetter) {
        // Fits the alphabetical sequence: (a), (b), (c)...
        expectedNextLetter = String.fromCharCode(marker.charCodeAt(0) + 1);
        if (expectedNextLetter.charCodeAt(0) > 'z'.charCodeAt(0)) expectedNextLetter = null;
        // This is a LETTER subclause - default, no marking needed
      } else {
        // Breaks alphabetical sequence - it's a roman numeral
        romanIndices.add(idx);
      }
    } else {
      // Multi-character: always a roman numeral (ii, iii, iv, vi, vii, viii, ix, xi, xii, etc.)
      romanIndices.add(idx);
    }
  }

  // Tag roman numeral paragraphs with a marker prefix
  const result = paragraphs.map((p, idx) => {
    if (romanIndices.has(idx)) {
      return '\u00A7\u00A7' + p; // §§ prefix for roman numerals
    }
    return p;
  });

  return result.join('\n\n');
}
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import { FileText, ExternalLink } from 'lucide-react';

// Helper: preprocess text to convert schedule references into markdown hyperlinks
// e.g. "Part A of First Schedule" → [Part A of First Schedule](/law-detail?id=dpdp-rules&schedule=1&part=A)
function addScheduleHyperlinks(text, lawId) {
  if (!text) return text;
  const ordinals = { first:1, second:2, third:3, fourth:4, fifth:5, sixth:6, seventh:7 };
  // Replace ordinal schedule references with markdown links
  let result = text.replace(
    /(\s)(?:Part\s+([A-Z])\s+of\s+(?:the\s+)?(First|Second|Third|Fourth|Fifth|Sixth|Seventh)\s+Schedule|(First|Second|Third|Fourth|Fifth|Sixth|Seventh)\s+Schedule)/gi,
    (match, ws, partLetter, ordinalWithPart, ordinalAlone) => {
      const ordinal = (ordinalWithPart || ordinalAlone).toLowerCase();
      const schedNum = ordinals[ordinal];
      if (!schedNum) return match;
      let url = `/law-detail?id=${lawId}&schedule=${schedNum}`;
      if (partLetter) url += `&part=${partLetter}`;
      const linkText = match.trimStart();
      return ` [${linkText}](${url})`;
    }
  );
  return result;
}

// Helper: convert "as may be prescribed" into clickable markdown links
// Only in clauses/sub-clauses that actually have a connected rule panel
function addPrescribedHyperlinks(text, articleRefs) {
  if (!text || !articleRefs.length) return text;

  // Build a lookup of exact positions that have connected rules
  // Key format: "clause" or "clause.subClause" or "clause.subClause.subSubClause"
  const refsPositions = new Set();
  let hasWholeRef = false;
  for (const ref of articleRefs) {
    if (ref.whole) { hasWholeRef = true; continue; }
    if (ref.clauseNum !== undefined) {
      // Always add the clause level
      let key = String(ref.clauseNum);
      if (ref.subClause !== undefined) {
        key += '.' + ref.subClause;
        if (ref.subSubClause !== undefined) {
          key += '.' + ref.subSubClause;
        }
      }
      refsPositions.add(key);
    }
    if (ref.renderBeforeClause !== undefined) {
      // The panel renders before clause N, so "as may be prescribed" is in clause N-1
      refsPositions.add(String(ref.renderBeforeClause - 1));
    }
    if (ref.renderBeforeIllustration && ref.afterSubClause) {
      // The panel renders before illustration, "as may be prescribed" is in that sub-clause
      refsPositions.add('null.' + ref.afterSubClause);
    }
  }

  const paragraphs = text.split('\n\n');
  let currentClause = null;
  let currentSubClause = null;
  let currentSubSubClause = null;
  let hasAnyNumberedClause = paragraphs.some(p => /^(?:\u00A7\u00A7)?\(\d+\)/.test(p.trim()));

  const result = paragraphs.map(p => {
    const trimmed = p.trim();
    const rawTrimmed = trimmed.startsWith('\u00A7\u00A7') ? trimmed.slice(2).trim() : trimmed;

    // Track numbered clause (1), (2), …
    const clauseMatch = rawTrimmed.match(/^\((\d+)\)/);
    if (clauseMatch) {
      currentClause = parseInt(clauseMatch[1]);
      currentSubClause = null;
      currentSubSubClause = null;
    }

    // Track lettered sub-clause (a), (b), …
    const subClauseMatch = !trimmed.startsWith('\u00A7\u00A7') && rawTrimmed.match(/^"?\(([a-z])\)\s/);
    if (subClauseMatch) {
      currentSubClause = subClauseMatch[1];
      currentSubSubClause = null;
    }

    // Track roman numeral sub-sub-clause §§(i), §§(ii), …
    if (trimmed.startsWith('\u00A7\u00A7')) {
      const romanMatch = rawTrimmed.match(/^"?\(([a-z]+)\)/);
      if (romanMatch) {
        currentSubSubClause = romanMatch[1];
      }
    }

    let shouldLink = false;

    if (!hasAnyNumberedClause && hasWholeRef) {
      // No numbered clauses and whole-article ref — link all
      shouldLink = true;
    } else if (!hasAnyNumberedClause && currentSubClause && refsPositions.has('null.' + currentSubClause)) {
      // No numbered clauses but sub-clause has a ref (e.g. renderBeforeIllustration)
      shouldLink = true;
    } else if (currentClause !== null) {
      // Check most specific position first, then broader
      if (currentSubSubClause && refsPositions.has(currentClause + '.' + currentSubClause + '.' + currentSubSubClause)) {
        shouldLink = true;
      } else if (currentSubClause && refsPositions.has(currentClause + '.' + currentSubClause)) {
        shouldLink = true;
      } else if (refsPositions.has(String(currentClause))) {
        // Only match clause-level if ref has no subClause specified
        shouldLink = true;
      }
    } else if (hasWholeRef) {
      shouldLink = true;
    }

    if (shouldLink && /as may be prescribed/.test(p)) {
      return p.replace(/(\s)as may be prescribed/g, () => {
        return ' [as may be prescribed](#open-connected-rule)';
      });
    }
    return p;
  });

  return result.join('\n\n');
}

// Helper: bold numbering markers like (1), (2), (a), (b), (i), (ii), (A), (B), etc.
function boldNumbering(text, restChildren) {
  // Match patterns at start: (1), (2), (a), (b), (i), (ii), (A), (B), (I), (II), etc.
  const match = text.match(/^(\([a-zA-Z0-9]+\))/);
  if (match) {
    const marker = match[1];
    const remainder = text.slice(marker.length);
    const rest = restChildren || [];
    return [<strong key="marker">{marker}</strong>, remainder, ...rest];
  }
  return null;
}

export default function ArticleContent({ law, chapterNum, articleNum, scheduleNum, schedulePart }) {
  // Scroll to top when chapter or article changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [chapterNum, articleNum]);
  if (!law) {
    return (
      <div className="flex items-center justify-center h-64 text-blue-500">
        Select a law to view its content
      </div>
    );
  }

  // Search for the specific chapter and article combination
  let foundChapter = null;
  let foundArticle = null;

  // Determine labels based on law type
  const isRules = law.id === 'dpdp-rules';
  const articleLabel = isRules ? 'Rule' : 'Section';
  const articleLabelShort = isRules ? 'Rule' : 'Sec.';
  const lawLabel = isRules ? 'DPDP Rules' : 'DPDP Act';

  // If a schedule is requested, render the schedule
  if (scheduleNum && law.schedules) {
    const schedule = law.schedules.find(s => s.number === scheduleNum);
    if (schedule) {
      // Custom renderer for subclauses
      const mdComponents = {
        a: ({ node, href, children, ...props }) => {
          if (href && href.startsWith('/')) {
            return <>{' '}<Link to={href} className="text-blue-600 hover:text-blue-800 underline font-medium">{children}</Link></>;
          }
          // Section 19 notify link: preserve whitespace before link so "may notify" is not concatenated into "maynotify"
          if (href === 'https://www.meity.gov.in/static/uploads/2025/11/f6c0837972422cf79d890bfe84cc04d6.pdf') {
            return <>{' '}<a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline font-medium" {...props}>{children}</a></>;
          }
          return <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline font-medium" {...props}>{children}</a>;
        },
        p({ children }) {
          const text = children[0];
          if (typeof text === 'string') {
            const trimmed = text.trim();
            // Illustration heading: italic and centered
            if (/^Illustrations?\.?$/.test(trimmed)) {
              return <p className="text-center italic my-4">{children}</p>;
            }
            // Check for §§ prefix (roman numeral marked by preprocessor)
            if (trimmed.startsWith('\u00A7\u00A7')) {
              const cleaned = trimmed.slice(2);
              const rest = Array.isArray(children) ? children.slice(1) : [];
              const bolded = boldNumbering(cleaned.trimStart(), rest);
              if (bolded) return <div className="subsubclause">{bolded}</div>;
              return <div className="subsubclause">{cleaned}{rest}</div>;
            }
            // Single lowercase letter in parens (optional leading quote): always a subclause
            if (/^"?\([a-z]\)\s/.test(trimmed)) {
              const rest = Array.isArray(children) ? children.slice(1) : [];
              const bolded = boldNumbering(trimmed, rest);
              if (bolded) return <div className="subclause">{bolded}</div>;
              return <div className="subclause">{children}</div>;
            }
            // Bold any numbering at the start of a regular paragraph
            const rest = Array.isArray(children) ? children.slice(1) : [];
            const bolded = boldNumbering(trimmed, rest);
            if (bolded) return <p>{bolded}</p>;
          }
          return <p>{children}</p>;
        },
      };

      const renderTable = (table) => {
        if (!table) return null;
        return (
          <div className="overflow-x-auto my-6">
            <table className="min-w-full border-collapse border border-gray-300 text-sm">
              <thead>
                <tr className="bg-gray-50">
                  {table.columns.map((col, idx) => (
                    <th key={idx} className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-800">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, rowIdx) => (
                  <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {table.columns.map((col, colIdx) => {
                      const key = col.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
                      const value = row[key] || row.s_no || '';
                      // Try to find the matching key
                      let cellValue = '';
                      if (colIdx === 0) cellValue = row.s_no;
                      else if (row.class !== undefined && colIdx === 1) cellValue = row.class;
                      else if (row.purposes !== undefined && col.toLowerCase().includes('purpose')) cellValue = row.purposes;
                      else if (row.conditions !== undefined && col.toLowerCase().includes('condition')) cellValue = row.conditions;
                      else if (row.time_period !== undefined && col.toLowerCase().includes('time')) cellValue = row.time_period;
                      else {
                        // fallback: try all keys
                        const keys = Object.keys(row);
                        cellValue = row[keys[colIdx]] || '';
                      }
                      return (
                        <td key={colIdx} className="border border-gray-300 px-3 py-2 text-gray-700 align-top whitespace-pre-line">
                          {cellValue}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      };

      const renderContent = (content) => {
        if (!content) return null;
        let formatted = content
          .split('\n')
          .map(line => line.replace(/^\s+/, ''))
          .join('\n');
        // Add line breaks before top-level clause markers; keep content rendering behavior as usual.
        formatted = formatted.replace(/"?(\([a-z]+\))\s/g, (match) => '\n\n' + match.trimEnd() + ' ');
        formatted = formatted.replace(/\n{3,}/g, '\n\n');
        // Classify markers as letters vs roman numerals
        formatted = classifyClauseMarkers(formatted);
        return <ReactMarkdown components={mdComponents}>{formatted}</ReactMarkdown>;
      };

      const renderPoints = (points) => {
        if (!points) return null;
        return (
          <div className="prose prose-gray max-w-none text-justify">
            <ol className="list-decimal list-inside space-y-6">
              {points.map((point, idx) => (
                <li key={idx} className="text-gray-700 mb-4">
                  {typeof point === 'string' ? (
                    point
                  ) : (
                    <div>
                      <span>{point.text}</span>
                      {point.subpoints && (
                        <ol className="list-[lower-alpha] list-inside ml-12 mt-3 space-y-3">
                          {point.subpoints.map((sub, subIdx) => (
                            <li key={subIdx} className="text-gray-600 mb-2">{sub}</li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </div>
        );
      };

      const isFourthScheduleB = Number(scheduleNum) === 4 && schedulePart === 'B';
      const renderNote = (note) => {
        if (!note) return null;

        if (isFourthScheduleB) {
          const lines = note
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0);
          const [intro, ...itemLines] = lines;
          const items = itemLines.map(line => {
            const match = line.match(/^("?\([a-z]+\))\s*(.*)$/);
            if (!match) return { marker: '', text: line };
            return { marker: match[1], text: match[2] };
          });

          return (
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Note</h3>
              <div className="prose max-w-none text-gray-700 text-justify text-sm">
                {intro && <p>{intro}</p>}
                {items.map((item, idx) => (
                  <div key={idx} className="subclause">
                    <strong>{item.marker}</strong> {item.text}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        let formatted = note
          .split('\n')
          .map(line => line.replace(/^\s+/, ''))
          .join('\n');
        formatted = formatted.replace(/(^|\n)("?\([a-z]+\))\s/g, (match, prefix, marker) => {
          return `${prefix}\n\n${marker} `;
        });
        formatted = formatted.replace(/\n{3,}/g, '\n\n');
        formatted = classifyClauseMarkers(formatted);
        return (
          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Note</h3>
            <div className="prose max-w-none text-gray-700 text-justify text-sm">
              <ReactMarkdown components={mdComponents}>{formatted}</ReactMarkdown>
            </div>
          </div>
        );
      };

      // If schedule has parts and a specific part is requested
      if (schedule.parts && schedulePart) {
        const part = schedule.parts.find(p => p.part === schedulePart);
        if (part) {
          return (
            <div className="article-content py-4 sm:py-8 flex justify-center px-3 sm:px-6" style={{ background: '#fff', minHeight: '100vh' }}>
              <div className="w-full max-w-2xl">
                <div className="gdpr-article-heading">
                  <div className="gdpr-article-number">{schedule.name}</div>
              <div className="text-xs text-gray-500 mb-1">[See {schedule.see_rule ? `rule ${schedule.see_rule}` : schedule.see_section ? `section ${schedule.see_section}` : 'rule'}]</div>
                  <div className="gdpr-article-title">Part {part.part} — {part.title}</div>
                </div>
                <div className="prose prose-gray max-w-none text-justify">
                  {part.content && renderContent(part.content)}
                  {part.points && renderPoints(part.points)}
                  {part.table && renderTable(part.table)}
                </div>
                {part.note && renderNote(part.note)}
                {schedule.note && renderNote(schedule.note)}
                <div className="text-center mt-8 pt-4 border-t border-gray-200">
                  <Link to={`/law-detail?id=${law.id}`} className="text-blue-700 hover:text-blue-900 hover:underline text-sm font-semibold">
                    {lawLabel}
                  </Link>
                  <br />
                  <Link to={`/law-detail?id=${law.id}`} className="text-blue-600 hover:text-blue-800 hover:underline text-xs">
                    Table of Contents
                  </Link>
                </div>
              </div>
            </div>
          );
        }
      }

      // Schedule without parts, or no specific part requested — show full schedule
      return (
        <div className="article-content py-4 sm:py-8 flex justify-center px-3 sm:px-6" style={{ background: '#fff', minHeight: '100vh' }}>
          <div className="w-full max-w-2xl">
            <div className="gdpr-article-heading">
              <div className="gdpr-article-number">{schedule.name}</div>
              <div className="text-xs text-gray-500 mb-1">[See {schedule.see_rule ? `rule ${schedule.see_rule}` : schedule.see_section ? `section ${schedule.see_section}` : 'rule'}]</div>
              {schedule.title && <div className="gdpr-article-title">{schedule.title}</div>}
            </div>
            <div className="prose prose-gray max-w-none text-justify">
              {schedule.content && renderContent(schedule.content)}
              {schedule.table && renderTable(schedule.table)}
              {schedule.parts && schedule.parts.map((part) => (
                <div key={part.part} className="mb-8">
                  <h3 className="text-lg font-bold text-gray-900 mt-6 mb-2">Part {part.part} — {part.title}</h3>
                  {part.content && renderContent(part.content)}
                  {part.points && renderPoints(part.points)}
                  {part.table && renderTable(part.table)}
                  {part.note && renderNote(part.note)}
                </div>
              ))}
            </div>
            {schedule.note && renderNote(schedule.note)}
            <div className="text-center mt-8 pt-4 border-t border-gray-200">
              <Link to={`/law-detail?id=${law.id}`} className="text-blue-700 hover:text-blue-900 hover:underline text-sm font-semibold">
                {lawLabel}
              </Link>
              <br />
              <Link to={`/law-detail?id=${law.id}`} className="text-blue-600 hover:text-blue-800 hover:underline text-xs">
                Table of Contents
              </Link>
            </div>
          </div>
        </div>
      );
    }
  }
  
  if (chapterNum && articleNum) {
    // First, try to find the exact chapter number match
    foundChapter = law.chapters?.find(c => String(c.number) === String(chapterNum));
    
    if (foundChapter) {
      // Look for the article within this specific chapter
      foundArticle = foundChapter.articles?.find(a => String(a.number) === String(articleNum));
    }
    
    // If not found in the specified chapter, search all chapters as fallback
    if (!foundArticle) {
      for (const chapter of law.chapters || []) {
        const article = chapter.articles?.find(a => String(a.number) === String(articleNum));
        if (article) {
          foundChapter = chapter;
          foundArticle = article;
          break;
        }
      }
    }
  }

  // If a specific article is selected, render it.
  if (foundChapter && foundArticle) {
    // Cross-ref context for this article
    const articleRefs = (isRules ? RULES_PRECISE_REFS : ACT_PRECISE_REFS)[foundArticle.number] || [];
    const wholeArticleRefs = articleRefs.filter(r => r.whole);
    const scheduleRefs = (!isRules ? (ACT_SCHEDULE_REFS[foundArticle.number] || []) : []);
    const wholeScheduleRefs = scheduleRefs.filter(r => r.whole);
    const otherData    = isRules ? dpdpActData   : dpdpRulesData;
    const otherPrefix  = isRules ? 'Section'      : 'Rule';
    const otherColor   = isRules ? '#1d4ed8'      : '#0891b2';
    const otherLabel   = isRules ? 'DPDP Act'     : 'DPDP Rules';

    // Mutable context tracker — updated by each paragraph as it renders
    const ctx = { clause: null, subClause: null, subSubClause: null };

    // Custom renderer for sub-clauses and preformatted blocks
    const mdComponents = {
      a: ({ node, href, children, ...props }) => {
        if (href === '#open-connected-rule') {
          return (
            <>{' '}<a
              href="#"
              className="text-blue-600 hover:text-blue-800 underline font-medium cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                // Find the nearest clause/paragraph element
                const clauseEl = e.target.closest('p, .subclause, .subsubclause');
                if (!clauseEl) return;
                // Search the prose container for the first RefPanel button AFTER this text
                const container = e.target.closest('.prose') || e.target.closest('.article-content');
                if (!container) return;
                const allButtons = Array.from(container.querySelectorAll('button'));
                let btn = allButtons.find(b => clauseEl.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING);
                // Fallback: check whole article container for whole-article refs
                if (!btn) {
                  const articleContainer = e.target.closest('.article-content');
                  if (articleContainer) {
                    const artButtons = Array.from(articleContainer.querySelectorAll('button'));
                    btn = artButtons.find(b => clauseEl.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING);
                  }
                }
                if (btn) {
                  btn.click();
                  setTimeout(() => btn.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
                }
              }}
            >
              {children}
            </a></>
          );
        }
        if (href && href.startsWith('/')) {
          return <>{' '}<Link to={href} className="text-blue-600 hover:text-blue-800 underline font-medium">{children}</Link></>;
        }
        if (href === 'https://www.meity.gov.in/static/uploads/2025/11/f6c0837972422cf79d890bfe84cc04d6.pdf') {
          return <>{' '}<a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline font-medium" {...props}>{children}</a></>;
        }
        return <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline font-medium" {...props}>{children}</a>;
      },
      p({ children }) {
        const text = children[0];
        if (typeof text === 'string') {
          const trimmed = text.trim();
          // Illustration heading: italic and centered
          if (/^Illustrations?\.?$/.test(trimmed)) {
            const beforeRefs = articleRefs.filter(r => r.renderBeforeIllustration && (!r.afterSubClause || r.afterSubClause === ctx.subClause));
            const beforeScheds = scheduleRefs.filter(r => r.renderBeforeIllustration && (!r.afterSubClause || r.afterSubClause === ctx.subClause));
            const ilEl = <p className="text-center italic my-4">{children}</p>;
            if (beforeRefs.length || beforeScheds.length) return <div>{beforeRefs.map((r,i) => <RefPanel key={'bi'+i} linkedNums={r.linkedNums} otherData={otherData} otherPrefix={otherPrefix} otherLabel={otherLabel} otherColor={otherColor} navigateMode={isRules} />)}{beforeScheds.map((r,i) => <ScheduleRefPanel key={'bis'+i} scheduleNum={r.scheduleNum} schedulePart={r.schedulePart} lawData={dpdpRulesData} />)}{ilEl}</div>;
            return ilEl;
          }
          // §§ prefix = roman numeral sub-sub-clause
          if (trimmed.startsWith('\u00A7\u00A7')) {
            const cleaned = trimmed.slice(2).trimStart();
            // Handle optional leading quote (e.g. "(i) quoted replacement text)
            const romanMatch = cleaned.match(/^"?\(([a-z]+)\)/);
            if (romanMatch) ctx.subSubClause = romanMatch[1];
            const rest = Array.isArray(children) ? children.slice(1) : [];
            const bolded = boldNumbering(cleaned, rest);
            const el = bolded
              ? <div className="subsubclause">{bolded}</div>
              : <div className="subsubclause">{cleaned}{rest}</div>;
            const matched = articleRefs.filter(r => matchesContext(r, ctx));
            const matchedSched = scheduleRefs.filter(r => matchesContext(r, ctx));
            if (matched.length || matchedSched.length) return <div>{el}{matched.map((r,i) => <RefPanel key={i} linkedNums={r.linkedNums} otherData={otherData} otherPrefix={otherPrefix} otherLabel={otherLabel} otherColor={otherColor} navigateMode={isRules} />)}{matchedSched.map((r,i) => <ScheduleRefPanel key={'s'+i} scheduleNum={r.scheduleNum} schedulePart={r.schedulePart} lawData={dpdpRulesData} />)}</div>;
            return el;
          }
          // Single lowercase letter in parens (optional leading quote): lettered sub-clause
          if (/^"?\([a-z]\)\s/.test(trimmed)) {
            const letter = trimmed.match(/^"?\(([a-z])\)/)[1];
            ctx.subClause = letter;
            ctx.subSubClause = null;
            const rest = Array.isArray(children) ? children.slice(1) : [];
            const bolded = boldNumbering(trimmed, rest);
            const el = bolded
              ? <div className="subclause">{bolded}</div>
              : <div className="subclause">{children}</div>;
            // Refs marked renderBeforeSubClause appear just before this sub-clause starts
            const beforeRefs = articleRefs.filter(r => r.renderBeforeSubClause === letter);
            const beforeScheds = scheduleRefs.filter(r => r.renderBeforeSubClause === letter);
            const matched = articleRefs.filter(r => matchesContext(r, ctx));
            const matchedSched = scheduleRefs.filter(r => matchesContext(r, ctx));
            if (beforeRefs.length || beforeScheds.length || matched.length || matchedSched.length) return <div>{beforeRefs.map((r,i) => <RefPanel key={'b'+i} linkedNums={r.linkedNums} otherData={otherData} otherPrefix={otherPrefix} otherLabel={otherLabel} otherColor={otherColor} navigateMode={isRules} />)}{beforeScheds.map((r,i) => <ScheduleRefPanel key={'bs'+i} scheduleNum={r.scheduleNum} schedulePart={r.schedulePart} lawData={dpdpRulesData} />)}{el}{matched.map((r,i) => <RefPanel key={i} linkedNums={r.linkedNums} otherData={otherData} otherPrefix={otherPrefix} otherLabel={otherLabel} otherColor={otherColor} navigateMode={isRules} />)}{matchedSched.map((r,i) => <ScheduleRefPanel key={'s'+i} scheduleNum={r.scheduleNum} schedulePart={r.schedulePart} lawData={dpdpRulesData} />)}</div>;
            return el;
          }
          // Numbered clause (1), (2), …
          const clauseMatch = trimmed.match(/^\((\d+)\)/);
          const rest = Array.isArray(children) ? children.slice(1) : [];
          const bolded = boldNumbering(trimmed, rest);
          const paraEl = bolded ? <p>{bolded}</p> : <p>{children}</p>;
          if (clauseMatch) {
            ctx.clause = parseInt(clauseMatch[1]);
            ctx.subClause = null;
            ctx.subSubClause = null;
            // Refs marked renderBeforeClause appear just before this clause starts
            const beforeRefs = articleRefs.filter(r => r.renderBeforeClause === ctx.clause);
            const beforeScheds = scheduleRefs.filter(r => r.renderBeforeClause === ctx.clause);
            const matched = articleRefs.filter(r => matchesContext(r, ctx));
            const matchedSched = scheduleRefs.filter(r => matchesContext(r, ctx));
            if (beforeRefs.length || beforeScheds.length || matched.length || matchedSched.length) return <div>{beforeRefs.map((r,i) => <RefPanel key={'b'+i} linkedNums={r.linkedNums} otherData={otherData} otherPrefix={otherPrefix} otherLabel={otherLabel} otherColor={otherColor} navigateMode={isRules} />)}{beforeScheds.map((r,i) => <ScheduleRefPanel key={'bs'+i} scheduleNum={r.scheduleNum} schedulePart={r.schedulePart} lawData={dpdpRulesData} />)}{paraEl}{matched.map((r,i) => <RefPanel key={i} linkedNums={r.linkedNums} otherData={otherData} otherPrefix={otherPrefix} otherLabel={otherLabel} otherColor={otherColor} navigateMode={isRules} />)}{matchedSched.map((r,i) => <ScheduleRefPanel key={'s'+i} scheduleNum={r.scheduleNum} schedulePart={r.schedulePart} lawData={dpdpRulesData} />)}</div>;
            return paraEl;
          }
          if (bolded) return <p>{bolded}</p>;
        }
        return <p>{children}</p>;
      },
      pre({ children }) {
        return <pre className="preformatted">{children}</pre>;
      }
    };
    // Preprocess: normalize content for proper formatting
    let formattedContent = foundArticle.content;
    // Split into lines and process each
    formattedContent = formattedContent
      .split('\n')
      .map(line => line.replace(/^\s+/, '')) // Remove all leading whitespace from each line
      .join('\n');
    // Ensure all clause markers start on new lines
    // Also absorb any leading " (opening quote) so it stays with its marker paragraph
    formattedContent = formattedContent.replace(/"?(\([a-z]+\))\s/g, (match) => '\n\n' + match.trimEnd() + ' ');
    // Remove extra blank lines
    formattedContent = formattedContent.replace(/\n{3,}/g, '\n\n');
    // Classify markers as letters vs roman numerals
    formattedContent = classifyClauseMarkers(formattedContent);
    // Convert schedule references to hyperlinks
    // For Rules: ordinal schedule refs (First Schedule, etc.)
    // For Act: bare "the Schedule" refs (→ Act's single schedule)
    if (isRules) {
      formattedContent = addScheduleHyperlinks(formattedContent, law.id);
    } else if (foundArticle.number === 33) {
      formattedContent = formattedContent.replace(
        /(\s)the Schedule(?![\w])/g,
        (match, ws) => ` [the Schedule](/law-detail?id=${law.id}&schedule=1)`
      );
    }
    // For Act sections, make "as may be prescribed" clickable to open connected rules
    if (!isRules && articleRefs.length > 0) {
      formattedContent = addPrescribedHyperlinks(formattedContent, articleRefs);
    }
    return (
      <div className="article-content py-4 sm:py-8 flex justify-center px-3 sm:px-6" style={{ background: '#fff', minHeight: '100vh' }}>
        <div className="w-full max-w-2xl">
          <div className="gdpr-article-heading">
            <div className="gdpr-article-number text-2xl font-bold text-center uppercase" style={{letterSpacing: '1px', marginBottom: '0.25em'}}>
              {articleLabel} {foundArticle.number}
            </div>
            <div className="gdpr-article-title text-center mb-2">{toTitleCase(foundArticle.title)}</div>
          </div>
          {foundArticle.recitals && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-300">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Recitals</h3>
              <div className="prose max-w-none text-gray-700 text-justify">
                <ReactMarkdown components={mdComponents}>{foundArticle.recitals}</ReactMarkdown>
              </div>
            </div>
          )}
          {foundArticle.key_issues && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-300">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Key issues</h3>
              <div className="prose max-w-none text-gray-700 text-justify">
                <ReactMarkdown components={mdComponents}>{Array.isArray(foundArticle.key_issues) ? foundArticle.key_issues.join('\n\n') : foundArticle.key_issues}</ReactMarkdown>
              </div>
            </div>
          )}
          <div className="prose prose-gray max-w-none text-justify">
            <ReactMarkdown components={mdComponents}>{formattedContent}</ReactMarkdown>
          </div>
          {/* Whole-article cross-reference (for articles like Section 14, 24 that map as a whole) */}
          {wholeArticleRefs.map((r, i) => (
            <RefPanel key={i} linkedNums={r.linkedNums} otherData={otherData} otherPrefix={otherPrefix}
              otherLabel={otherLabel} otherColor={otherColor} navigateMode={isRules} />
          ))}
          {/* Whole-article schedule cross-references */}
          {wholeScheduleRefs.map((r, i) => (
            <ScheduleRefPanel key={'ws'+i} scheduleNum={r.scheduleNum} schedulePart={r.schedulePart} lawData={dpdpRulesData} />
          ))}

          {/* Previous / Next Section Navigation */}
          {(() => {
            // Build flat list of all articles with their chapter info
            const allArticles = [];
            law.chapters?.forEach(ch => {
              ch.articles?.forEach(art => {
                allArticles.push({ chapter: ch.number, article: art.number, title: art.title });
              });
            });
            const currentIdx = allArticles.findIndex(
              a => String(a.chapter) === String(chapterNum) && String(a.article) === String(articleNum)
            );
            const prev = currentIdx > 0 ? allArticles[currentIdx - 1] : null;
            const next = currentIdx < allArticles.length - 1 ? allArticles[currentIdx + 1] : null;
            return (
              <div className="mt-8 sm:mt-12 pt-4 sm:pt-6 border-t-2 border-blue-200">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0">
                  <div>
                    {prev && (
                      <Link
                        to={`/law-detail?id=${law.id}&chapter=${prev.chapter}&article=${prev.article}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
                      >
                        ← {articleLabelShort} {prev.article} {isRules ? 'Rules' : 'DPDP'}
                      </Link>
                    )}
                  </div>
                  <div>
                    {next && (
                      <Link
                        to={`/law-detail?id=${law.id}&chapter=${next.chapter}&article=${next.article}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
                      >
                        {articleLabelShort} {next.article} {isRules ? 'Rules' : 'DPDP'} →
                      </Link>
                    )}
                  </div>
                </div>
                <div className="text-center mt-5 mb-2 space-y-1">
                  <Link
                    to={`/law-detail?id=${law.id}`}
                    className="text-blue-700 hover:text-blue-900 hover:underline text-sm font-semibold"
                  >
                    {lawLabel}
                  </Link>
                  <br />
                  <Link
                    to={`/law-detail?id=${law.id}`}
                    className="text-blue-600 hover:text-blue-800 hover:underline text-xs"
                  >
                    Table of Contents
                  </Link>
                </div>
              </div>
            );
          })()}
          {/* Author's Summary Box */}
          <div className="mx-auto mt-4 mb-3 max-w-xs bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border border-blue-200 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02] cursor-default">
            <div className="px-4 py-2.5 text-center">
              <div className="flex items-center justify-center mb-1">
                <svg className="w-4 h-4 text-blue-600 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-base font-bold text-blue-800 tracking-wide">Author's Summary</h3>
              </div>
              <div className="w-10 h-0.5 bg-gradient-to-r from-blue-400 to-purple-500 mx-auto mb-2 rounded-full"></div>
              <p className="text-gray-600 font-medium text-xs leading-relaxed">
                Coming soon
              </p>
              <div className="mt-1 text-xs text-gray-500 italic">
                Detailed insights & analysis
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No specific article requested — show homepage with improved layout
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, Arial, Helvetica, sans-serif' }}>
      <div className="max-w-3xl mx-auto px-3 sm:px-6 py-4 sm:py-6 pt-8 sm:pt-16 pb-4">
        {/* Title and Description */}
        <div className="text-center mb-6">
             <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
            {law.title}
          </h1>
          <div className="max-w-2xl mx-auto bg-gray-50 rounded-md p-4 border-l-4 border-blue-400 mt-8 mb-8 shadow-sm">
            <p className="text-base md:text-lg text-gray-800 leading-relaxed" style={{ textAlign: 'justify' }}>
              {isRules
                ? 'Rules made in exercise of powers conferred by sub-sections (1) and (2) of section 40 of the Digital Personal Data Protection Act, 2023 (22 of 2023), for the implementation and enforcement of data protection provisions.'
                : 'An Act to provide for the processing of digital personal data in a manner that recognises both the right of individuals to protect their personal data and the need to process such personal data for lawful purposes and for matters connected therewith or incidental thereto.'
              }
            </p>
          </div>
        </div>

        {/* Quick Access Section */}
        <div className="mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
            Quick Access
          </h2>
          <div className="bg-white border border-gray-200 rounded-md shadow-sm divide-y divide-gray-100">
            {law.chapters?.map((ch, idx) => (
              <div key={ch.number} className="flex items-center px-2 py-1">
                <div className="text-sm md:text-base text-gray-700 pr-2 whitespace-nowrap font-medium">
                  <Link 
                    to={`/law-detail?id=${law.id}&chapter=${ch.number}`} 
                    className="text-blue-600 hover:underline"
                  >
                    Chapter {ch.number}
                  </Link>
                  <span className="mx-2 text-gray-300">|</span>
                </div>
                <div className="flex-1 flex flex-wrap gap-x-2 gap-y-1 items-center">
                  {ch.articles?.map((art, artIdx) => (
                    <React.Fragment key={art.number}>
                      <Link 
                        to={`/law-detail?id=${law.id}&chapter=${ch.number}&article=${art.number}`} 
                        className="text-blue-600 hover:underline text-sm md:text-base px-1"
                      >
                        {art.number}
                      </Link>
                      {artIdx < ch.articles.length - 1 && <span className="text-gray-200">•</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
            {/* Schedule Quick Access */}
            {law.schedules?.map((schedule) => (
              <div key={`sched-${schedule.number}`} className="flex items-center px-2 py-1">
                <div className="text-sm md:text-base text-gray-700 pr-2 whitespace-nowrap font-medium">
                  <Link 
                    to={`/law-detail?id=${law.id}&schedule=${schedule.number}`} 
                    className="text-blue-600 hover:underline"
                  >
                    {schedule.name}
                  </Link>
                  {schedule.parts && (
                    <>
                      <span className="mx-2 text-gray-300">|</span>
                      {schedule.parts.map((part, pIdx) => (
                        <React.Fragment key={part.part}>
                          <Link
                            to={`/law-detail?id=${law.id}&schedule=${schedule.number}&part=${part.part}`}
                            className="text-blue-600 hover:underline text-sm px-1"
                          >
                            Part {part.part}
                          </Link>
                          {pIdx < schedule.parts.length - 1 && <span className="text-gray-200">•</span>}
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Table of Contents Section */}
        <div className="mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
            Table of Contents
          </h2>
          <div className="space-y-4">
            {law.chapters?.map((ch) => (
              <div key={ch.number} className="border-b border-gray-100 pb-3 last:border-b-0">
                <div className="flex items-baseline mb-1">
                  <span className="text-base font-bold text-gray-800 mr-2">Chapter {ch.number}</span>
                  <span className="text-gray-300 mx-1">|</span>
                  <span className="text-base font-semibold text-gray-800">{ch.name}</span>
                </div>
                <div className="space-y-1 ml-12">
                  {ch.articles?.map((art) => (
                    <div key={art.number} className="flex items-baseline hover:bg-gray-50 px-2 py-1 rounded transition-colors">
                      <Link 
                        to={`/law-detail?id=${law.id}&chapter=${ch.number}&article=${art.number}`}
                        className="text-blue-600 hover:underline font-medium mr-2 whitespace-nowrap text-sm md:text-base"
                      >
                        {articleLabel} {art.number}
                      </Link>
                      <span className="text-gray-300 mx-1">|</span>
                      <Link 
                        to={`/law-detail?id=${law.id}&chapter=${ch.number}&article=${art.number}`}
                        className="text-gray-700 hover:text-blue-700 flex-1 text-sm md:text-base"
                      >
                        {art.title}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Schedules in TOC */}
            {law.schedules?.length > 0 && (
              <div className="border-t-2 border-gray-200 pt-4 mt-4">
                <h3 className="text-lg font-bold text-gray-800 mb-3">Schedules</h3>
                {law.schedules.map((schedule) => (
                  <div key={`toc-sched-${schedule.number}`} className="border-b border-gray-100 pb-3 mb-3 last:border-b-0">
                    <div className="flex items-baseline mb-1">
                      <Link
                        to={`/law-detail?id=${law.id}&schedule=${schedule.number}`}
                        className="text-base font-bold text-blue-600 hover:underline mr-2"
                      >
                        {schedule.name}
                      </Link>
                      {schedule.see_rule && (
                        <span className="text-xs text-gray-500">[See rule {schedule.see_rule}]</span>
                      )}
                    </div>
                    {schedule.title && (
                      <div className="ml-12 text-sm text-gray-700 mb-1">{schedule.title}</div>
                    )}
                    {schedule.parts && (
                      <div className="space-y-1 ml-12">
                        {schedule.parts.map((part) => (
                          <div key={part.part} className="flex items-baseline hover:bg-gray-50 px-2 py-1 rounded transition-colors">
                            <Link
                              to={`/law-detail?id=${law.id}&schedule=${schedule.number}&part=${part.part}`}
                              className="text-blue-600 hover:underline font-medium mr-2 whitespace-nowrap text-sm"
                            >
                              Part {part.part}
                            </Link>
                            <span className="text-gray-300 mx-1">|</span>
                            <Link
                              to={`/law-detail?id=${law.id}&schedule=${schedule.number}&part=${part.part}`}
                              className="text-gray-700 hover:text-blue-700 flex-1 text-sm"
                            >
                              {part.title}
                            </Link>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {law.official_pdf_url && (
          <div className="text-center mt-6 pt-4 border-t border-gray-100">
            <a 
              href={law.official_pdf_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
            >
              <FileText className="w-5 h-5" />
              <span>Download Official PDF</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}